import type { components } from "./_generated/schema";
import { apiFetch } from "./apiFetch";

export type ProjectOut = components["schemas"]["ProjectOut"];
export type ProjectListOut = components["schemas"]["ProjectListOut"];
export type CreateProjectIn = components["schemas"]["CreateProjectIn"];
export type PipelineOut = components["schemas"]["PipelineOut"];
export type PipelineListOut = components["schemas"]["PipelineListOut"];
export type CreatePipelineIn = components["schemas"]["CreatePipelineIn"];

export function listProjects(token: string): Promise<ProjectListOut> {
  return apiFetch<ProjectListOut>("/api/v1/projects", { token });
}

export function getProject(token: string, projectId: string): Promise<ProjectOut> {
  return apiFetch<ProjectOut>(`/api/v1/projects/${encodeURIComponent(projectId)}`, { token });
}

export function createProject(token: string, input: CreateProjectIn): Promise<ProjectOut> {
  return apiFetch<ProjectOut>("/api/v1/projects", { method: "POST", token, json: input });
}

export function listPipelines(token: string, projectId: string): Promise<PipelineListOut> {
  return apiFetch<PipelineListOut>(`/api/v1/projects/${encodeURIComponent(projectId)}/pipelines`, {
    token,
  });
}

export function createPipeline(
  token: string,
  projectId: string,
  input: CreatePipelineIn,
): Promise<PipelineOut> {
  return apiFetch<PipelineOut>(`/api/v1/projects/${encodeURIComponent(projectId)}/pipelines`, {
    method: "POST",
    token,
    json: input,
  });
}

export function getPipeline(token: string, pipelineId: string): Promise<PipelineOut> {
  return apiFetch<PipelineOut>(`/api/v1/pipelines/${encodeURIComponent(pipelineId)}`, { token });
}

export type LifecycleTransitionIn = components["schemas"]["LifecycleTransitionIn"];

/** Move a pipeline along its lifecycle. Promotion into Validation or Production
 *  is Administrator-only server-side (ADR-0017) — the UI hides those options for
 *  everyone else rather than offering an action the backend would refuse. */
export function transitionPipelineLifecycle(
  token: string,
  pipelineId: string,
  input: LifecycleTransitionIn,
): Promise<PipelineOut> {
  return apiFetch<PipelineOut>(`/api/v1/pipelines/${encodeURIComponent(pipelineId)}/lifecycle`, {
    method: "POST",
    token,
    json: input,
  });
}

export type EditorStateOut = components["schemas"]["EditorStateOut"];
export type VersionListOut = components["schemas"]["VersionListOut"];
export type VersionGraphOut = components["schemas"]["VersionGraphOut"];
export type RunOut = components["schemas"]["RunOut"];
export type RunListOut = components["schemas"]["RunListOut"];
export type RunSummaryOut = components["schemas"]["RunSummaryOut"];
export type ScheduleOut = components["schemas"]["ScheduleOut"];
export type BlobWatchOut = components["schemas"]["BlobWatchOut"];

const pipelineBase = (id: string) => `/api/v1/pipelines/${encodeURIComponent(id)}`;

/** The canvas init state: the latest saved version's graph (or empty when
 *  nothing is saved yet). There is no draft — unsaved edits live only here. */
export function getEditorState(token: string, pipelineId: string): Promise<EditorStateOut> {
  return apiFetch<EditorStateOut>(`${pipelineBase(pipelineId)}/editor`, { token });
}

/** List saved versions (newest first). */
export function listVersions(token: string, pipelineId: string): Promise<VersionListOut> {
  return apiFetch<VersionListOut>(`${pipelineBase(pipelineId)}/versions`, { token });
}

/** Load one saved version's graph. */
export function getVersion(
  token: string,
  pipelineId: string,
  versionNo: number,
): Promise<VersionGraphOut> {
  return apiFetch<VersionGraphOut>(`${pipelineBase(pipelineId)}/versions/${versionNo}`, { token });
}

/** Snapshot the submitted canvas as a new immutable version (audited, deduped
 *  against the latest). The only way a graph is persisted. */
export function saveVersion(
  token: string,
  pipelineId: string,
  graph: Record<string, unknown>,
): Promise<{ version_no: number }> {
  return apiFetch<{ version_no: number }>(`${pipelineBase(pipelineId)}/versions`, {
    method: "POST",
    token,
    json: { graph },
  });
}

export type PredictPreviewOut = components["schemas"]["PredictPreviewOut"];

/** Test an AI · Predict metadata node: the node and its (read-only) upstream
 *  run once, capped at the first three documents. Model calls are recorded as
 *  in a run; nothing is written. */
export function previewPredictMetadata(
  token: string,
  pipelineId: string,
  graph: Record<string, unknown>,
  nodeId: string,
): Promise<PredictPreviewOut> {
  return apiFetch<PredictPreviewOut>(`${pipelineBase(pipelineId)}/predict-metadata/preview`, {
    method: "POST",
    token,
    json: { graph, node_id: nodeId },
  });
}

/** Run the submitted canvas now (save-then-run → a new version), and start the
 *  schedule if the pipeline has one.
 *
 *  `parameters` fills the `{{name}}` references the graph declares. Omitting it
 *  means "use the defaults"; a required parameter with no value is refused
 *  before anything is queued. */
export function runPipeline(
  token: string,
  pipelineId: string,
  graph: Record<string, unknown>,
  parameters?: Record<string, string>,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${pipelineBase(pipelineId)}/runs`, {
    method: "POST",
    token,
    json: parameters ? { graph, parameters } : { graph },
  });
}

/** Pause the pipeline's schedule (no-op if it has none). */
export function stopPipeline(token: string, pipelineId: string): Promise<ScheduleOut> {
  return apiFetch<ScheduleOut>(`${pipelineBase(pipelineId)}/stop`, { method: "POST", token });
}

/** Ask a run in flight to stop. 202 — it stops at its next checkpoint, so the
 *  caller should keep polling the run rather than assume it is already over. */
export function stopRun(token: string, pipelineId: string, runId: string): Promise<unknown> {
  return apiFetch<unknown>(`${pipelineBase(pipelineId)}/runs/${runId}/stop`, {
    method: "POST",
    token,
  });
}

/** Rename a project. Audited server-side with the old and new name. */
export function renameProject(
  token: string,
  projectId: string,
  name: string,
  description: string | null,
): Promise<ProjectOut> {
  return apiFetch<ProjectOut>(`/api/v1/projects/${encodeURIComponent(projectId)}`, {
    method: "PATCH",
    token,
    json: { name, description },
  });
}

/** Rename a pipeline. Only while it is editable — a promoted pipeline's name is
 *  what its validation record refers to. */
export function renamePipeline(
  token: string,
  pipelineId: string,
  name: string,
  description: string | null,
): Promise<PipelineOut> {
  return apiFetch<PipelineOut>(pipelineBase(pipelineId), {
    method: "PATCH",
    token,
    json: { name, description },
  });
}

/** Recent run history (newest first). */
/** `background` marks a poll (ADR-0046): it does not keep the session alive. */
export interface PollOpts {
  background?: boolean;
}

export function listRuns(
  token: string,
  pipelineId: string,
  opts: PollOpts = {},
): Promise<RunListOut> {
  return apiFetch<RunListOut>(`${pipelineBase(pipelineId)}/runs`, {
    token,
    background: opts.background,
  });
}

export type RecentRunOut = components["schemas"]["RecentRunOut"];
export type RecentRunListOut = components["schemas"]["RecentRunListOut"];

/** Finished runs from the last week across every pipeline the caller can see,
 *  newest first. Feeds the notification bell, which has no pipeline in hand —
 *  it asks what has happened lately, not what happened to one pipeline. */
export function listRecentRuns(
  token: string,
  opts: { background?: boolean } = {},
): Promise<RecentRunListOut> {
  return apiFetch<RecentRunListOut>("/api/v1/runs", { token, background: opts.background });
}

/** Current scheduler state (for the countdown). */
export function getSchedule(
  token: string,
  pipelineId: string,
  opts: PollOpts = {},
): Promise<ScheduleOut> {
  return apiFetch<ScheduleOut>(`${pipelineBase(pipelineId)}/schedule`, {
    token,
    background: opts.background,
  });
}

/** Current Blob-arrival watch state (for the node's watching indicator). */
export function getBlobWatch(
  token: string,
  pipelineId: string,
  opts: PollOpts = {},
): Promise<BlobWatchOut> {
  return apiFetch<BlobWatchOut>(`${pipelineBase(pipelineId)}/blob-watch`, {
    token,
    background: opts.background,
  });
}

/** Mantine badge colour for a pipeline lifecycle state. */
/** Re-exported from the semantic palette (`shared/ui/palette.ts`). */
export { lifecycleColor } from "@/shared/ui/palette";

export type SolutionPageIn = components["schemas"]["SolutionPageIn"];

/** "Show results in": publish a pipeline's run results to a Solution page, or
 *  stop (`null`). Developer+ and owner/`edit` server-side (ADR-0032); audited. */
export function setPipelineSolutionPage(
  token: string,
  pipelineId: string,
  solutionPage: string | null,
): Promise<PipelineOut> {
  return apiFetch<PipelineOut>(
    `/api/v1/pipelines/${encodeURIComponent(pipelineId)}/solution-page`,
    { method: "PUT", token, json: { solution_page: solutionPage } satisfies SolutionPageIn },
  );
}
